import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { RecognizedTimezones } from '@modernloop/shared/datetime';

const LOCAL_STORAGE_KEY = 'LAST_USED_TIMEZONES';
const MAX_LAST_USED_TIMEZONES = 10;

/** Actions available for managing last used timezones */
interface LastUsedTimezonesActions {
  /** Add a timezone to the list (moves to front if already exists) */
  add: (timezone: string) => void;
  /** Remove a specific timezone from the list */
  remove: (timezone: string) => void;
  /** Clear all last used timezones */
  clear: () => void;
}

type LastUsedTimezonesContextValue = [string[], LastUsedTimezonesActions];

const defaultActions: LastUsedTimezonesActions = {
  add: () => {},
  remove: () => {},
  clear: () => {},
};

const LastUsedTimezonesContext = createContext<LastUsedTimezonesContextValue>([[], defaultActions]);

interface LastUsedTimezonesProviderProps {
  children?: ReactNode;
}

/**
 * Validates if a timezone string is a recognized timezone
 * @param timezone - The timezone string to validate
 * @returns true if the timezone is valid, false otherwise
 */
const isValidTimezone = (timezone: unknown): timezone is string => {
  if (typeof timezone !== 'string' || timezone.trim() === '') {
    return false;
  }
  return RecognizedTimezones.includes(timezone);
};

/**
 * Safely parses and validates timezones from localStorage
 * @returns An array of valid timezone strings, or empty array if invalid/not found
 */
const restoreTimezones = (): string[] => {
  try {
    const storedData = window.localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!storedData) {
      return [];
    }

    const parsed: unknown = JSON.parse(storedData);

    // Validate that parsed data is an array
    if (!Array.isArray(parsed)) {
      console.warn('LastUsedTimezonesProvider: Invalid data format in localStorage, expected array');
      return [];
    }

    // Filter to only valid timezone strings
    const validTimezones = parsed.filter(isValidTimezone);

    // If we had to filter out invalid entries, update localStorage
    if (validTimezones.length !== parsed.length) {
      console.warn('LastUsedTimezonesProvider: Filtered out invalid timezone entries from localStorage');
      try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(validTimezones));
      } catch {
        // Ignore storage errors during cleanup
      }
    }

    // Ensure we don't exceed max limit
    return validTimezones.slice(0, MAX_LAST_USED_TIMEZONES);
  } catch (err) {
    // JSON.parse failed or other error
    console.warn('LastUsedTimezonesProvider: Failed to parse localStorage data', err);
    return [];
  }
};

/**
 * Safely stores timezones to localStorage
 * @param timezones - Array of timezone strings to store
 */
const storeTimezones = (timezones: string[]): void => {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(timezones));
  } catch (err) {
    // localStorage might be full or disabled
    console.warn('LastUsedTimezonesProvider: Failed to save to localStorage', err);
  }
};

/**
 * Provider component for managing last used timezones with localStorage persistence.
 *
 * @example
 * ```tsx
 * <LastUsedTimezonesProvider>
 *   <App />
 * </LastUsedTimezonesProvider>
 * ```
 */
export const LastUsedTimezonesProvider: FC<LastUsedTimezonesProviderProps> = ({ children }) => {
  const [timezones, setTimezones] = useState<string[]>([]);

  // Initialize from localStorage on mount
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const restored = restoreTimezones();
    setTimezones(restored);
  }, []);

  const add = useCallback((timezone: string) => {
    // Validate the timezone before adding
    if (!isValidTimezone(timezone)) {
      console.warn(`LastUsedTimezonesProvider: Attempted to add invalid timezone "${timezone}"`);
      return;
    }

    setTimezones((prev) => {
      // Create new array
      const updated = [...prev];

      // Check if timezone already exists
      const index = updated.indexOf(timezone);

      // If it's already at the front, no change needed
      if (index === 0) {
        return prev;
      }

      // Remove from current position if exists
      if (index !== -1) {
        updated.splice(index, 1);
      }

      // Add to front
      updated.unshift(timezone);

      // Truncate to max limit
      const result = updated.slice(0, MAX_LAST_USED_TIMEZONES);

      // Persist to localStorage
      storeTimezones(result);

      return result;
    });
  }, []);

  const remove = useCallback((timezone: string) => {
    if (typeof timezone !== 'string' || timezone.trim() === '') {
      return;
    }

    setTimezones((prev) => {
      const index = prev.indexOf(timezone);

      // If not found, no change needed
      if (index === -1) {
        return prev;
      }

      const updated = [...prev];
      updated.splice(index, 1);

      // Persist to localStorage
      storeTimezones(updated);

      return updated;
    });
  }, []);

  const clear = useCallback(() => {
    setTimezones([]);
    storeTimezones([]);
  }, []);

  const actions = useMemo<LastUsedTimezonesActions>(
    () => ({
      add,
      remove,
      clear,
    }),
    [add, remove, clear]
  );

  const value = useMemo<LastUsedTimezonesContextValue>(() => [timezones, actions], [timezones, actions]);

  return <LastUsedTimezonesContext.Provider value={value}>{children}</LastUsedTimezonesContext.Provider>;
};

/**
 * Hook to access and manage last used timezones.
 *
 * @returns A tuple containing:
 * - `lastUsedTimezones`: Array of timezone strings (always at least an empty array, never null/undefined)
 * - `actions`: Object with `add`, `remove`, and `clear` functions
 *
 * @example
 * ```tsx
 * const [lastUsedTimezones, { add, remove, clear }] = useLastUsedTimezones();
 *
 * // Add a timezone (moves to front if already exists)
 * add('America/Los_Angeles');
 *
 * // Remove a specific timezone
 * remove('America/New_York');
 *
 * // Clear all timezones
 * clear();
 * ```
 */
export const useLastUsedTimezones = (): LastUsedTimezonesContextValue => {
  const context = useContext(LastUsedTimezonesContext);

  if (context === undefined) {
    // Return safe default if used outside provider (shouldn't happen but be resilient)
    console.warn('useLastUsedTimezones must be used within a LastUsedTimezonesProvider');
    return [[], defaultActions];
  }

  return context;
};
